import { ref } from "vue";

// Lightweight Web Audio synth for game SFX. No assets required.
// The AudioContext is created lazily on the first user-triggered sound
// (browsers block audio before a user gesture).

const MUTE_KEY = "candle-clash-muted";

function readMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

const muted = ref(typeof window !== "undefined" ? readMuted() : false);

let ctx: AudioContext | null = null;
let master: GainNode | null = null;

function ensure(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.35;
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function toggleMute() {
  muted.value = !muted.value;
  try {
    window.localStorage.setItem(MUTE_KEY, muted.value ? "1" : "0");
  } catch {
    // Storage unavailable — mute state just won't persist.
  }
}

interface ToneOpts {
  freq: number;
  endFreq?: number;
  duration?: number;
  type?: OscillatorType;
  volume?: number;
  delay?: number;
}

function tone({
  freq,
  endFreq,
  duration = 0.15,
  type = "sine",
  volume = 0.3,
  delay = 0,
}: ToneOpts) {
  const ac = ensure();
  if (!ac || !master || muted.value) return;
  const t0 = ac.currentTime + delay;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (endFreq)
    osc.frequency.exponentialRampToValueAtTime(endFreq, t0 + duration);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(volume, t0 + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
  osc.connect(gain);
  gain.connect(master);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
}

interface NoiseOpts {
  duration?: number;
  volume?: number;
  delay?: number;
  filterFreq?: number;
}

function noise({
  duration = 0.08,
  volume = 0.3,
  delay = 0,
  filterFreq = 3000,
}: NoiseOpts = {}) {
  const ac = ensure();
  if (!ac || !master || muted.value) return;
  const t0 = ac.currentTime + delay;
  const buffer = ac.createBuffer(
    1,
    Math.ceil(ac.sampleRate * duration),
    ac.sampleRate
  );
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = ac.createBufferSource();
  src.buffer = buffer;
  const filter = ac.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = filterFreq;
  const gain = ac.createGain();
  gain.gain.setValueAtTime(volume, t0);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(master);
  src.start(t0);
}

// --- SFX ---
const click = () =>
  tone({ freq: 640, duration: 0.06, type: "triangle", volume: 0.5 });
const chip = () =>
  tone({ freq: 520, duration: 0.05, type: "triangle", volume: 0.35 });

function cardFlip() {
  noise({ duration: 0.05, volume: 0.22, filterFreq: 2400 });
  tone({
    freq: 340,
    endFreq: 560,
    duration: 0.08,
    type: "triangle",
    volume: 0.22,
  });
}

function tickUp() {
  tone({
    freq: 440,
    endFreq: 660,
    duration: 0.12,
    type: "square",
    volume: 0.2,
  });
  tone({
    freq: 660,
    endFreq: 880,
    duration: 0.12,
    type: "square",
    volume: 0.16,
    delay: 0.09,
  });
}

function tickDown() {
  tone({
    freq: 440,
    endFreq: 300,
    duration: 0.12,
    type: "square",
    volume: 0.2,
  });
  tone({
    freq: 300,
    endFreq: 210,
    duration: 0.14,
    type: "square",
    volume: 0.16,
    delay: 0.09,
  });
}

const tickFlat = () => tone({ freq: 440, duration: 0.1, volume: 0.25 });
const spike = () =>
  tone({
    freq: 200,
    endFreq: 900,
    duration: 0.3,
    type: "sawtooth",
    volume: 0.18,
  });
const correction = () =>
  tone({
    freq: 520,
    endFreq: 180,
    duration: 0.4,
    type: "sawtooth",
    volume: 0.18,
  });

function moon() {
  [523, 659, 784, 1047, 1319].forEach((f, i) =>
    tone({
      freq: f,
      duration: 0.18,
      type: "triangle",
      volume: 0.28,
      delay: i * 0.09,
    })
  );
}

function crash() {
  tone({
    freq: 400,
    endFreq: 60,
    duration: 0.6,
    type: "sawtooth",
    volume: 0.32,
  });
  noise({ duration: 0.5, volume: 0.22, filterFreq: 900 });
}

function win() {
  [659, 784, 988, 1319].forEach((f, i) =>
    tone({
      freq: f,
      duration: i === 3 ? 0.35 : 0.14,
      type: "triangle",
      volume: 0.3,
      delay: i * 0.11,
    })
  );
}

function lose() {
  tone({ freq: 220, endFreq: 90, duration: 0.5, volume: 0.4 });
  tone({ freq: 110, duration: 0.4, volume: 0.22, delay: 0.05 });
}

function offer() {
  tone({ freq: 880, duration: 0.1, volume: 0.3 });
  tone({ freq: 660, duration: 0.1, volume: 0.3, delay: 0.14 });
  tone({ freq: 880, duration: 0.14, volume: 0.3, delay: 0.28 });
}

function cashout() {
  [988, 1319, 988, 1319].forEach((f, i) =>
    tone({
      freq: f,
      duration: 0.07,
      type: "square",
      volume: 0.2,
      delay: i * 0.06,
    })
  );
}

const marketOpen = () =>
  tone({ freq: 660, endFreq: 880, duration: 0.2, volume: 0.3 });

function error() {
  tone({ freq: 160, duration: 0.25, type: "square", volume: 0.28 });
  tone({
    freq: 120,
    duration: 0.25,
    type: "square",
    volume: 0.22,
    delay: 0.02,
  });
}

export function useSound() {
  return {
    muted,
    toggleMute,
    click,
    chip,
    cardFlip,
    tickUp,
    tickDown,
    tickFlat,
    spike,
    correction,
    moon,
    crash,
    win,
    lose,
    offer,
    cashout,
    marketOpen,
    error,
  };
}
